import { MAX_SEARCH_TERMS_BYTES } from '../../../constants.js';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function keywordText(entry) {
  if (typeof entry === 'string') return entry;
  if (!entry || typeof entry !== 'object') return '';
  return entry.keyword || entry.term || entry.word || '';
}

function searchVolume(entry) {
  if (!entry || typeof entry !== 'object') return 0;
  return Number(entry.searches30d || entry.searches || entry.volume || entry['30天搜索量'] || 0) || 0;
}

function normalizeKeyword(value) {
  return String(value || '').trim().toLowerCase();
}

const LOW_VALUE_RECOMMENDATION_TERMS = new Set([
  'a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'of', 'on', 'or', 'the', 'to', 'with',
  'made', 'make', 'use', 'uses', 'used', 'using', 'us', 'usa', 'item', 'items', 'product', 'products',
  'supplies', 'supply', 'material', 'materials', 'craft', 'crafts', 'art', 'arts', 'decorations',
  'free',
]);

function selectedKeywordList(selectedStKeywords) {
  if (selectedStKeywords instanceof Set) return [...selectedStKeywords];
  return asArray(selectedStKeywords);
}

function conflictItems(conflictResult) {
  if (!conflictResult || typeof conflictResult !== 'object') return [];
  return asArray(conflictResult.conflicts || conflictResult.items || conflictResult.risks);
}

function policyLabel(policy) {
  const labels = {
    must_use: '必须写入',
    can_use: '可写入',
    backend_only: '后台词',
    ads_only: '广告测试',
    forbidden: '禁用',
  };
  return labels[policy] || policy || '未分级';
}

function policyRank(policy) {
  const ranks = {
    must_use: 4,
    can_use: 3,
    backend_only: 2,
    ads_only: 1,
    forbidden: 0,
  };
  return ranks[policy] ?? -1;
}

function buildPolicyLookup(keywordUsePolicies) {
  const lookup = new Map();
  for (const item of asArray(keywordUsePolicies)) {
    const keyword = normalizeKeyword(item?.keyword || item?.normalizedKeyword || item?.term || item?.word || '');
    if (!keyword || lookup.has(keyword)) continue;
    lookup.set(keyword, item);
  }
  return lookup;
}

function isUsefulRecommendationKeyword(item) {
  const keyword = String(item?.keyword || '').trim();
  const normalized = normalizeKeyword(keyword);
  if (!normalized || normalized.length < 3) return false;
  if (LOW_VALUE_RECOMMENDATION_TERMS.has(normalized)) return false;
  if (/^[\d\W_]+$/.test(normalized)) return false;

  const tokenCount = normalized.split(/\s+/).filter(Boolean).length;
  if (tokenCount === 1 && (item?.searches30d || 0) < 50 && (item?.score || 0) < 0.35) {
    return false;
  }

  return true;
}

function isUsefulFallbackKeyword(item) {
  if (!isUsefulRecommendationKeyword(item)) return false;
  const tokenCount = normalizeKeyword(String(item?.keyword || '')).split(/\s+/).filter(Boolean).length;
  const volume = Number(item?.searches30d || 0) || 0;
  return tokenCount > 1 || volume >= 500;
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0));
}

export function buildKeywordSummaryModel({
  keywordLibrary,
  selectedStKeywords,
  byteCount = 0,
  conflictResult = null,
  keywordUsePolicies = [],
  businessContext = null,
} = {}) {
  const keywords = asArray(keywordLibrary?.keywords);
  const selected = selectedKeywordList(selectedStKeywords).filter(Boolean);
  const selectedSet = new Set(selected.map(normalizeKeyword).filter(Boolean));
  const estimatedSearchVolume = keywords.reduce((total, entry) => {
    const text = normalizeKeyword(keywordText(entry));
    if (!text || !selectedSet.has(text)) return total;
    return total + searchVolume(entry);
  }, 0);
  const searchTermsBytes = Math.max(0, Number(byteCount || 0));
  const hasConflictResult = !!conflictResult && typeof conflictResult === 'object';
  const conflicts = conflictItems(conflictResult);
  const conflictCount = conflicts.length;
  const checkStatus = String(conflictResult?.checkStatus || '');
  const conflictDecision = String(conflictResult?.decision || '');
  const conflictReviewRequired = conflictDecision === 'review_required'
    || ['degraded', 'unavailable', 'insufficient_context', 'request_failed', 'stale'].includes(checkStatus);
  const conflictCheckDegraded = Boolean(conflictResult?.degraded) || conflictReviewRequired;
  const policyLookup = buildPolicyLookup(keywordUsePolicies || businessContext?.keywordUsePolicies || []);
  const selectedKeywordSet = new Set(selected.map((item) => normalizeKeyword(item)).filter(Boolean));
  const recommendationBuckets = {
    must_use: [],
    can_use: [],
    backend_only: [],
    ads_only: [],
    forbidden: [],
    unclassified: [],
  };

  for (const entry of keywords) {
    const keyword = keywordText(entry);
    const normalized = normalizeKeyword(keyword);
    if (!normalized) continue;
    const policy = policyLookup.get(normalized);
    const baseItem = {
      keyword,
      normalizedKeyword: normalized,
      searches30d: searchVolume(entry),
      score: Number(entry?.relevanceScore || entry?.score || 0) || 0,
      selected: selectedKeywordSet.has(normalized),
      policy: policy?.policy || 'unclassified',
      reason: policy?.reason || '',
      preferredFields: asArray(policy?.preferredFields),
      blockedFields: asArray(policy?.blockedFields),
    };

    if (baseItem.policy in recommendationBuckets) {
      recommendationBuckets[baseItem.policy].push(baseItem);
    } else {
      recommendationBuckets.unclassified.push(baseItem);
    }
  }

  const recommendedKeywords = [
    ...recommendationBuckets.must_use,
    ...recommendationBuckets.can_use,
    ...recommendationBuckets.backend_only,
  ]
    .filter(isUsefulRecommendationKeyword)
    .sort((a, b) => {
      const rankDiff = policyRank(b.policy) - policyRank(a.policy);
      if (rankDiff !== 0) return rankDiff;
      const selectedDiff = Number(b.selected) - Number(a.selected);
      if (selectedDiff !== 0) return selectedDiff;
      const volumeDiff = (b.searches30d || 0) - (a.searches30d || 0);
      if (volumeDiff !== 0) return volumeDiff;
      return (b.score || 0) - (a.score || 0);
    })
    .slice(0, 10);

  const fallbackKeywords = recommendationBuckets.unclassified
    .concat(recommendationBuckets.ads_only)
    .filter(isUsefulFallbackKeyword)
    .sort((a, b) => (b.searches30d || 0) - (a.searches30d || 0) || (b.score || 0) - (a.score || 0))
    .slice(0, 8);

  const excludedKeywords = recommendationBuckets.forbidden
    .filter(isUsefulRecommendationKeyword)
    .sort((a, b) => (b.searches30d || 0) - (a.searches30d || 0))
    .slice(0, 6);

  const policyCounts = {
    must_use: recommendationBuckets.must_use.length,
    can_use: recommendationBuckets.can_use.length,
    backend_only: recommendationBuckets.backend_only.length,
    ads_only: recommendationBuckets.ads_only.length,
    forbidden: recommendationBuckets.forbidden.length,
  };

  return {
    selectedCount: selected.length,
    totalKeywordCount: keywords.length,
    searchTermsBytes,
    searchTermsLimit: MAX_SEARCH_TERMS_BYTES,
    remainingBytes: Math.max(0, MAX_SEARCH_TERMS_BYTES - searchTermsBytes),
    isOverLimit: searchTermsBytes > MAX_SEARCH_TERMS_BYTES,
    estimatedSearchVolume,
    conflictCount,
    conflicts,
    hasConflictResult,
    conflictDecision,
    conflictReviewRequired,
    conflictCheckDegraded,
    conflictWarning: String(conflictResult?.warning || ''),
    conflictLevel: conflictCount > 0 || conflictReviewRequired ? 'warning' : 'ok',
    conflictLabel: conflictCount > 0
      ? `${conflictCount} 项风险`
      : conflictReviewRequired
        ? '需要重试'
        : checkStatus === 'skipped'
          ? '未检测'
          : hasConflictResult ? '已通过' : '待检测',
    recommendation: {
      policyCounts,
      recommendedKeywords,
      fallbackKeywords,
      excludedKeywords,
      summaryText: policyLookup.size
        ? `系统按卖点与策略自动推荐，优先 ${policyLabel('backend_only')} / ${policyLabel('can_use')}，自动排除 ${policyLabel('forbidden')}。`
        : '当前还没有关键词策略，系统先按词库相关性和搜索量做基础推荐。',
      recommendationCount: recommendedKeywords.length,
      fallbackCount: fallbackKeywords.length,
      excludedCount: excludedKeywords.length,
      hasPolicies: policyLookup.size > 0,
      policySummary: businessContext?.keywordPolicySummary || '',
    },
  };
}

function createElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createKeywordPills(keywords) {
  const list = Array.isArray(keywords) ? keywords.filter(Boolean) : [];
  if (!list.length) {
    return [createElement('span', 'keyword-summary-empty', '尚未选择关键词')];
  }
  return list.map((keyword) => createElement('span', 'keyword-summary-pill', keyword));
}

function createConflictList(conflicts) {
  const list = Array.isArray(conflicts) ? conflicts.filter(Boolean) : [];
  if (!list.length) return null;
  const card = createElement('div', 'keyword-summary-conflict-card');
  card.setAttribute('role', 'alert');
  card.setAttribute('aria-label', 'AI 冲突结果');
  const hero = createElement('div', 'keyword-summary-conflict-hero');
  const copy = createElement('div', 'keyword-summary-conflict-copy');
  copy.append(
    createElement('div', 'keyword-summary-conflict-title', 'AI 风控提醒：检测到关键词与产品卖点冲突'),
    createElement('div', 'keyword-summary-conflict-subtitle', '以下关键词与产品卖点存在语义互斥，建议先剔除再继续生成。'),
  );
  hero.append(createElement('div', 'keyword-summary-conflict-icon', '⚠️'), copy);
  const conflictList = createElement('div', 'keyword-summary-conflict-list');
  list.forEach((conflict) => {
    const item = createElement('div', 'keyword-summary-conflict-item');
    const row = createElement('div', 'keyword-summary-conflict-row');
    row.append(
      createElement('span', 'keyword-summary-conflict-badge', '互斥词'),
      createElement('strong', 'keyword-summary-conflict-keyword', conflict.keyword || ''),
      createElement('span', 'keyword-summary-conflict-label', '与你的卖点冲突:'),
      createElement('strong', 'keyword-summary-conflict-seller', `"${conflict.sellingPoint || ''}"`),
    );
    const reason = createElement('div', 'keyword-summary-conflict-reason');
    reason.append(createElement('strong', '', 'AI 诊断原因：'), document.createTextNode(String(conflict.reason || '')));
    item.append(row, reason);
    conflictList.append(item);
  });
  card.append(hero, conflictList);
  return card;
}

function createConflictDegraded(summary) {
  if (!summary?.conflictReviewRequired) return null;
  const card = createElement('div', 'keyword-summary-conflict-card');
  card.setAttribute('role', 'status');
  card.setAttribute('aria-label', '关键词终审降级提醒');
  const hero = createElement('div', 'keyword-summary-conflict-hero');
  const copy = createElement('div', 'keyword-summary-conflict-copy');
  copy.append(
    createElement('div', 'keyword-summary-conflict-title', '关键词智能终审暂未完成'),
    createElement('div', 'keyword-summary-conflict-subtitle', summary.conflictWarning || '请补充产品事实或稍后重试；在完成前不会显示为“无风险”。'),
  );
  hero.append(createElement('div', 'keyword-summary-conflict-icon', '⏳'), copy);
  card.append(hero);
  return card;
}

function createKeywordList(keywords, emptyText) {
  const list = Array.isArray(keywords) ? keywords.filter(Boolean) : [];
  if (!list.length) {
    return [createElement('span', 'keyword-summary-empty', emptyText || '暂无推荐')];
  }
  return list.map((item) => {
    const pill = createElement('span', `keyword-summary-rec-pill${item.selected ? ' selected' : ''}`);
    if (item.policy && item.policy !== 'unclassified') {
      pill.append(createElement('span', 'keyword-summary-rec-badge', policyLabel(item.policy)));
    }
    pill.append(document.createTextNode(String(item.keyword || '')));
    return pill;
  });
}

function createRecommendationPanel(recommendation) {
  if (!recommendation) return null;
  const policyCounts = recommendation.policyCounts || {};
  const topRecommended = recommendation.recommendedKeywords || [];
  const fallbackKeywords = recommendation.fallbackKeywords || [];
  const excludedKeywords = recommendation.excludedKeywords || [];
  const panel = createElement('div', 'keyword-summary-recommendation-panel');
  const head = createElement('div', 'keyword-summary-recommendation-head');
  const copy = document.createElement('div');
  copy.append(
    createElement('div', 'keyword-summary-recommendation-kicker', '系统推荐结果'),
    createElement('div', 'keyword-summary-recommendation-title', '按卖点与策略自动筛词，优先推荐可写入后台词'),
    createElement('div', 'keyword-summary-recommendation-copy', recommendation.summaryText || ''),
  );
  const metrics = createElement('div', 'keyword-summary-recommendation-metrics');
  metrics.append(
    createElement('span', 'keyword-summary-recommendation-metric', `推荐 ${formatNumber(recommendation.recommendationCount || 0)}`),
    createElement('span', 'keyword-summary-recommendation-metric muted', `观察 ${formatNumber(recommendation.fallbackCount || 0)}`),
    createElement('span', 'keyword-summary-recommendation-metric muted', `排除 ${formatNumber(recommendation.excludedCount || 0)}`),
  );
  head.append(copy, metrics);
  const grid = createElement('div', 'keyword-summary-recommendation-grid');
  [
    ['推荐采纳词', topRecommended, '暂无推荐采纳词'],
    ['观察备选词', fallbackKeywords, '暂无观察词'],
    ['已排除词', excludedKeywords, '暂无排除词'],
  ].forEach(([title, items, emptyText]) => {
    const card = createElement('div', 'keyword-summary-recommendation-card');
    const row = createElement('div', 'keyword-summary-recommendation-pill-row');
    row.append(...createKeywordList(items, emptyText));
    card.append(createElement('div', 'keyword-summary-recommendation-card-title', title), row);
    grid.append(card);
  });
  const strip = createElement('div', 'keyword-summary-policy-strip');
  [
    ['必须写入', policyCounts.must_use], ['可写入', policyCounts.can_use], ['后台词', policyCounts.backend_only],
    ['广告测试', policyCounts.ads_only], ['禁用', policyCounts.forbidden],
  ].forEach(([label, count]) => strip.append(createElement('span', 'keyword-summary-policy-chip', `${label} ${formatNumber(count || 0)}`)));
  panel.append(head, grid, strip);
  return panel;
}

export function renderKeywordSummary(model) {
  const summary = model || buildKeywordSummaryModel();
  const selectedKeywords = Array.isArray(summary.selectedKeywords) ? summary.selectedKeywords.filter(Boolean) : [];
  const selectedPreview = selectedKeywords.slice(0, 6);
  const selectionMode = summary.selectionMode === 'manual' ? 'manual' : 'ai';

  const root = createElement('section', 'keyword-summary-shell keyword-summary-shell-compact');
  root.setAttribute('aria-label', 'Search Terms 状态条');
  const hero = createElement('div', 'keyword-summary-hero');
  const copy = document.createElement('div');
  copy.append(
    createElement('div', 'keyword-summary-kicker', 'Step 02 · Search Terms 构建器'),
    createElement('h3', 'keyword-summary-title', '左边选词，右边实时生成 Search Terms'),
    createElement('p', 'keyword-summary-copy', selectionMode === 'ai'
      ? '系统会根据产品卖点、关键词策略和搜索量自动帮你推荐词；下方高级区的准入策略会继续影响这里的推荐结果。'
      : '当前是自己选择模式，直接在左侧候选词池里点选关键词即可。'),
  );
  const actions = createElement('div', 'keyword-summary-actions');
  const switcher = createElement('div', 'keyword-summary-mode-switch');
  switcher.setAttribute('role', 'tablist');
  switcher.setAttribute('aria-label', '系统推荐或自己选择');
  const aiButton = createElement('button', `keyword-summary-mode-btn${selectionMode === 'ai' ? ' active' : ''}`, '系统推荐');
  aiButton.id = 'st-mode-ai';
  aiButton.type = 'button';
  const manualButton = createElement('button', `keyword-summary-mode-btn${selectionMode === 'manual' ? ' active' : ''}`, '自己选择');
  manualButton.id = 'st-mode-manual';
  manualButton.type = 'button';
  switcher.append(aiButton, manualButton);
  actions.append(switcher, createElement('span', 'keyword-summary-mode-hint', selectionMode === 'ai' ? '按策略自动推荐并预选关键词' : '手动点选，当前布局不变'));
  hero.append(copy, actions);

  const strip = createElement('div', 'keyword-summary-strip');
  strip.setAttribute('aria-label', 'Search Terms 摘要');
  const createChip = (label, value, className = 'keyword-summary-chip', hintNodes = []) => {
    const chip = createElement('div', className);
    chip.append(createElement('span', 'keyword-summary-label', label), createElement('strong', 'keyword-summary-value', value));
    if (hintNodes.length) {
      const hint = createElement('span', 'keyword-summary-hint');
      hint.append(...hintNodes);
      chip.append(hint);
    }
    return chip;
  };
  strip.append(
    createChip('总关键词', formatNumber(summary.totalKeywordCount)),
    createChip('已选', formatNumber(summary.selectedCount), 'keyword-summary-chip', createKeywordPills(selectedPreview)),
    createChip('Search Terms', `${formatNumber(summary.searchTermsBytes)} / ${formatNumber(summary.searchTermsLimit)} bytes`, `keyword-summary-chip ${summary.isOverLimit ? 'is-over-limit' : 'is-ok'}`, [createElement('span', '', summary.isOverLimit ? `超出 ${formatNumber(summary.searchTermsBytes - summary.searchTermsLimit)} bytes` : `剩余 ${formatNumber(summary.remainingBytes)} bytes`)]),
    createChip('预计覆盖搜索量', formatNumber(summary.estimatedSearchVolume)),
    createChip('冲突风险', summary.conflictLabel, `keyword-summary-chip ${summary.conflictLevel === 'warning' ? 'has-warning' : 'is-ok'}`),
  );
  root.append(hero);
  const recommendation = createRecommendationPanel(summary.recommendation);
  if (recommendation) root.append(recommendation);
  root.append(strip);
  const conflicts = createConflictList(summary.conflicts);
  if (conflicts) root.append(conflicts);
  const degraded = createConflictDegraded(summary);
  if (degraded) root.append(degraded);
  return root;
}

export function renderKeywordSummaryInto(container, input) {
  if (!container) return;
  const model = input && input.selectedCount != null ? input : buildKeywordSummaryModel(input);
  const selectedKeywords = input?.selectedStKeywords instanceof Set
    ? [...input.selectedStKeywords]
    : Array.isArray(input?.selectedStKeywords)
      ? input.selectedStKeywords
      : [];
  container.replaceChildren(renderKeywordSummary({
    ...model,
    selectedKeywords: selectedKeywords.slice(0, 10),
    selectionMode: input?.selectionMode,
    searchTermsText: String(input?.searchTermsText || '').trim(),
  }));
}
